import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getDynamicFieldView } from '../../DynamicFieldView/store/selectors/getDynamicFieldView';
import ErrorBoundaryWrapper from '@gameday/components/ErrorFallback/ErrorBoundaryWrapper';
import DynamicFieldViewDataContainer from '../../DynamicFieldView/containers/DynamicFieldViewDataContainer';
import FieldVisionButtonDataContainer from '../../FieldVision/containers/FieldVisionButtonDataContainer';
import PitchFxDataContainer from '../../PitchFx/PitchFxDataContainer';
import { CarouselOverride, LiveDFVWrapper, DFVWrapper } from '../LiveDFV.style';
import CustomDot from '../components/CustomDot';
import useTracking from '@gameday/utils/hooks/useTracking';
import { ButtonTypes } from '../../FieldVision/components/Button';
import PlayTimerProvider from '@gameday/utils/providers/PlayTimerProvider';
import DFVWithChipFeed from '../components/DFVWithChipFeed';
import { ChipFeedDataContainer } from '../../ChipFeed';
import { ChipFeedWrapper } from '../Live.style';

const responsive = {
    everything: {
        breakpoint: { max: 4000, min: 0 },
        items: 1
    }
};

const viewNames = ['Batter', 'Dynamic Field'];
const clickNames = ['Batter', 'Field'];

const slideChangeHandler = (viewIndex, Tracking, setShowDFVReplay) => {
    const viewName = viewNames[viewIndex];
    const trackingCfg = {
        componentName: ['Live'],
        activity: viewName + ' View Click',
        text: clickNames[viewIndex]
    };
    Tracking.track.track_click(trackingCfg);
    setShowDFVReplay(!!(viewName === 'Batter'));
};

const LiveViewsDFVDataContainer = ({
    onAuthOpen,
    type,
    firstRunUser,
    showExplore,
    nativeLoginFunc
}) => {
    const { enableDFVMobile, inWarmupWindow, disableDfvPlay } = useSelector(getDynamicFieldView);

    const [isWarmup, setIsWarmup] = useState(null);
    const Tracking = useTracking();
    const [disablePlayResults, setDisablePlayResults] = useState(false);
    const [showDFVReplay, setShowDFVReplay] = useState(true);

    useEffect(() => Tracking.track.track_dfv_pageload(), []);

    let warmupTwoMins;
    function warmupTimer () {
        const twoMins = 120000;
        clearTimeout(warmupTwoMins);
        warmupTwoMins = setTimeout(() => {
            setIsWarmup(false);
        }, twoMins);
    }

    useEffect(() => {
        if (inWarmupWindow) {
            setIsWarmup(true);
            warmupTimer();
        }
    }, [enableDFVMobile, inWarmupWindow]);

    useEffect(() => {
        if (enableDFVMobile && !disableDfvPlay) {
            setDisablePlayResults(true);
        }
    }, [enableDFVMobile, disableDfvPlay]);

    const LiveBundle = useMemo(() => {
        return (<>
            <FieldVisionButtonDataContainer buttonType={nativeLoginFunc && ButtonTypes.NATIVELOGIN} showPreview={nativeLoginFunc || onAuthOpen} $type={type} firstRunUser={firstRunUser} showExplore={showExplore} />
            <PitchFxDataContainer viewType={type} showMidInning={true} />
            <ChipFeedWrapper><ChipFeedDataContainer /></ChipFeedWrapper>
        </>);
    }, [onAuthOpen, type, firstRunUser, showExplore, nativeLoginFunc, ButtonTypes, disablePlayResults]);

    const DFVBundle = useMemo(() => {
        return enableDFVMobile && !isWarmup
            ? (
                <PlayTimerProvider adjusted={true}>
                    <CarouselOverride
                        layout={responsive}
                        draggable={false}
                        mobileSwipeable={true}
                        autoPlay={false}
                        arrowsOutside={false}
                        showDots={true}
                        showArrows={false}
                        afterChange={(nextSlide, { currentSlide }) => slideChangeHandler(currentSlide, Tracking, setShowDFVReplay)}CustomDotComponent={<CustomDot />}
                    >
                        {LiveBundle}
                        <DFVWrapper $viewType={type}>
                            <DynamicFieldViewDataContainer renderStandalone={true} />
                        </DFVWrapper>
                    </CarouselOverride>
                    { showDFVReplay && <DFVWithChipFeed /> }
                </PlayTimerProvider>
            )
            : LiveBundle;
    }, [enableDFVMobile, isWarmup, LiveBundle, Tracking, type, showDFVReplay]);

    return (
        <ErrorBoundaryWrapper componentName='Live'>
            <LiveDFVWrapper>
                { DFVBundle }
            </LiveDFVWrapper>
        </ErrorBoundaryWrapper>
    );
};

LiveViewsDFVDataContainer.propTypes = {
    onAuthOpen: PropTypes.func,
    type: PropTypes.string,
    firstRunUser: PropTypes.bool,
    showExplore: PropTypes.func,
    nativeLoginFunc: PropTypes.func
};

export default LiveViewsDFVDataContainer;
